import { Button, Flex, Space, Stack } from "@mantine/core";
import { isEmail, isNotEmpty, useForm } from "@mantine/form";
import { addMessage } from "@soluto-private/expert-workspace-timeline";
import type { JSX } from "react";
import { useState } from "react";
import { getRootDispatcher, withPolymorphicClickAnalytics } from "@/analytics";
import { HandshakeAnimation } from "@/common-ui/animations/handshake";
import { useVerifiedCustomerStore } from "@/customer-info";
import { assistMessageSuggestions, type Intent, useIVRInfo } from "@/customer-info/api";
import { Header } from "@/customer-panel/components/call-intro-flow/steps/Header";
import type { StepProps } from "@/customer-panel/components/call-intro-flow/steps/models";
import { NavButtons } from "@/customer-panel/components/call-intro-flow/steps/NavButtons";
import { SalesQuestionnaire } from "@/customer-panel/components/call-intro-flow/steps/sales-questionnaire/SalesQuestionnaire";
import { isEnoughInfoGatheredToDetermineEligibility } from "@/customer-panel/components/call-intro-flow/utils/isEnoughInfoGatheredToDetermineEligibility";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { isVoiceTask, useMostRecentTask, useSessionId } from "@/sdk";
import { isInternationalCaller } from "@/shared-utils";
import { CallReasonForm } from "./CallReasonForm";
import { CustomerInfoForm } from "./CustomerInfoForm";
import { IVRSummary } from "./IVRSummary";

// TODO: HMW make this more configurable per partner depending on what IVR info is available & what customer info is needed
export function CustomerInfoCollectionStep({ onNext, onPrev }: StepProps): JSX.Element {
    const sessionId = useSessionId();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const setCustomer = useVerifiedCustomerStore((state) => state.setCustomer);
    const ivrInfo = useIVRInfo();
    const task = useMostRecentTask();
    const voiceTask = isVoiceTask(task) ? task : null;
    const hasInfoFromIVR = !!ivrInfo.intent || !!ivrInfo.summary;
    const {
        features: { isSalesQuestionnaireEnabled = false },
        salesQuestionnaireProps: { plans } = {},
    } = usePartnerConfig();

    const [intent, setIntent] = useState<Intent | undefined>(customer?.intent ?? (ivrInfo.intent as Intent));
    const ButtonWithAnalytics = withPolymorphicClickAnalytics(Button<"button">, "Click");

    const form = useForm({
        validateInputOnBlur: true,
        initialValues: {
            email: customer?.email ?? ivrInfo.email ?? "",
            firstName: customer?.firstName ?? ivrInfo.firstName ?? "",
            lastName: customer?.lastName ?? ivrInfo.lastName ?? "",
            isInternationalCaller: voiceTask?.mdn ? isInternationalCaller(voiceTask?.mdn) : false,
            currentPlan: customer?.productName ?? null,
        },
        validate: {
            email: isEmail("Invalid email"),
            firstName: isNotEmpty("Enter the customer’s first name"),
            lastName: isNotEmpty("Enter the customer’s last name"),
        },
    });

    const handleNext =
        intent && onNext
            ? () => {
                  setCustomer(sessionId, {
                      ...customer,
                      ...form.getValues(),
                      fullName: `${form.getValues().firstName} ${form.getValues().lastName}`,
                      intent,
                      mdn: voiceTask?.mdn,
                      productName: form.getValues().currentPlan ?? undefined,
                      metadata: {
                          isInternationalCaller: form.getValues().isInternationalCaller,
                      },
                  });
                  addMessage(sessionId, {
                      id: crypto.randomUUID(),
                      text: assistMessageSuggestions[intent],
                      type: "Default",
                      isUnread: true,
                      status: "active",
                      key: "intentDetected",
                  });
                  onNext();
                  void getRootDispatcher().dispatchBusinessEvent("SalesQuestionnaireInfoGathered", {
                      isInternationalCaller: form.getValues().isInternationalCaller,
                      currentPlan: form.getValues().currentPlan,
                  });
                  void getRootDispatcher().dispatchBusinessEvent("CustomerIntentCollected", {
                      intent,
                  });
              }
            : undefined;

    return (
        <Stack mt="sm" w="100%">
            {hasInfoFromIVR && (
                <Stack align="center">
                    <HandshakeAnimation width="40%" />
                    <Header text="IVR handoff" />
                </Stack>
            )}
            {ivrInfo.intent && intent ? (
                <Stack gap={0}>
                    <IVRSummary intent={intent} summary={ivrInfo.summary} />
                    <Flex w="100%" justify="flex-end">
                        <ButtonWithAnalytics
                            variant="subtle"
                            c="dark.0"
                            td="underline"
                            p="0"
                            onClick={() => setIntent(undefined)}
                            analytics={{ actionId: "IncorrectCallReason" }}
                        >
                            Change reason
                        </ButtonWithAnalytics>
                    </Flex>
                </Stack>
            ) : (
                <CallReasonForm intent={intent} setIntent={setIntent} />
            )}
            <Stack gap={0} w="100%">
                <CustomerInfoForm form={form} />
                <Space h="md" />
                {plans && (
                    <>
                        <SalesQuestionnaire form={form} plans={plans} />
                        <Space h="md" />
                    </>
                )}
                <NavButtons
                    onNext={
                        form.isValid() &&
                        isEnoughInfoGatheredToDetermineEligibility(isSalesQuestionnaireEnabled, form.values) &&
                        intent &&
                        onNext
                            ? handleNext
                            : undefined
                    }
                    onPrev={onPrev}
                />
            </Stack>
        </Stack>
    );
}
